// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ShareOptions } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import WishListSharingStatus, {
    WishListSharingStatusStyles,
} from "@insite/content-library/Components/WishListSharingStatus";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Star from "@insite/mobius/Icons/Star";
import StarX from "@insite/mobius/Icons/StarX";
import Tag from "@insite/mobius/Icons/Tag";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setListLoadingAction from "@synd/App/Handlers/MyListDetails/setListLoadingAction";
import SetInsufficientModalIOpen from "@synd/App/Handlers/XNG/SetInsufficientModalIOpen";
import Tooltip from "@synd/MobiusOverrides/Tooltip/Tooltip";
import React, { useContext } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    allowEditingOfWishLists: getSettingsCollection(state).wishListSettings.allowEditingOfWishLists,
    isUpdatingCart: state.context.isUpdatingCart,
    // XNG-Change
    wishListURL: getPageLinkByPageType(state, "MyListsDetailsPage")?.url,
});

// XNG-Changes
const mapDispatchToProps = { setListLoadingAction, SetInsufficientModalIOpen };

interface OwnProps {
    wishList: WishListModel;
    extendedStyles?: WishListCardStyles;
    // XNG-Changes: add ? to mark it as optional
    addWishListToCart?: HandleThunkActionCreator<typeof addWishListToCart>;
    deleteWishList?: () => void;
    leaveWishList?: () => void;
    toggleFavorite?: () => void;
    toggleHide?: () => void;
    // XNG-Change
    isMiniView?: boolean;
}

// XNG-Changes: add mapDispatchToProps
type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface WishListCardStyles {
    gridContainer?: GridContainerProps;
    wishListInfoGridItem?: GridItemProps;
    wishListInfoGridContainer?: GridContainerProps;
    basicInfoGridItem?: GridItemProps;
    basicInfoGridContainer?: GridContainerProps;
    detailLinkGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyProps;
    lastUpdatedGridItem?: GridItemProps;
    lastUpdatedText?: TypographyProps;
    extendedInfoGridItem?: GridItemProps;
    extendedInfoGridContainer?: GridContainerProps;
    productImagesGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    productImage?: LazyImageProps;
    sharingStatusGridItem?: GridItemProps;
    sharingStatus?: WishListSharingStatusStyles;
    actionGridItem?: GridItemProps;
    actionGridMediumHidden?: HiddenProps;
    actionOverflowIcon?: IconPresentationProps;
    actionOverflowMenu?: OverflowMenuPresentationProps;
    actionAddToCartClickable?: ClickablePresentationProps;
    actionHideClickable?: ClickablePresentationProps;
    actionDeleteClickable?: ClickablePresentationProps;
    actionLeaveClickable?: ClickablePresentationProps;
    actionAddToCartButton?: ButtonPresentationProps;
    actionHideLink?: LinkPresentationProps;
    actionDeleteButton?: ButtonPresentationProps;
    actionLeaveButton?: ButtonPresentationProps;
    actionFavoriteClickable?: ClickablePresentationProps;
    basicIconGridContainer?: GridContainerProps;
    detailIconGridItem?: GridItemProps;
    starIcon?: IconPresentationProps;
    starXIcon?: IconPresentationProps;
    tagTooltip?: TooltipPresentationProps;
    // XNG-Changes
    cardContainer?: InjectableCss;
    title?: TypographyPresentationProps;
    miniLastUpdatedText?: TypographyPresentationProps;
    miniProductImage?: LazyImageProps;
    container?: InjectableCss;
    descriptionWrapper?: InjectableCss;
    imagesWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    sharingStatusWrapper?: InjectableCss;
    nameWrapper?: InjectableCss;
    favTagsWrapper?: InjectableCss;
    tagIcon?: IconPresentationProps;
    actionDeleteLink?: LinkPresentationProps;
    actionLeaveLink?: LinkPresentationProps;
    itemsSmallHeading?: SmallHeadingAndTextStyles;
    additionalActions?: InjectableCss;
}

export const wishListCardStyles: WishListCardStyles = {
    gridContainer: {
        gap: 5,
    },
    wishListInfoGridItem: {
        width: [10, 10, 8, 10, 10],
        align: "middle",
    },
    basicInfoGridItem: { width: [12, 12, 5, 4, 4] },
    basicInfoGridContainer: {
        gap: 10,
        css: css`
            margin-left: 10px;
        `,
    },
    detailLinkGridItem: { width: 12 },
    descriptionGridItem: { width: 12 },
    descriptionText: { ellipsis: true },
    lastUpdatedGridItem: { width: 12 },
    extendedInfoGridItem: {
        width: [12, 12, 7, 8, 8],
        align: "middle",
    },
    productImageLink: {},
    productImagesGridItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            flex-wrap: wrap;
        `,
    },
    productImage: {
        css: css`
            max-width: 75px;
            img {
                height: 100%;
            }
        `,
        errorTypographyProps: {
            size: 11,
            css: css`
                max-height: 65px;
            `,
        },
    },
    sharingStatusGridItem: { width: [12, 12, 12, 4, 4] },
    actionGridItem: {
        width: [2, 2, 4, 2, 2],
        align: "top",
        css: css`
            justify-content: flex-end;
        `,
    },
    actionGridMediumHidden: {
        css: css`
            width: 100%;
        `,
    },
    actionAddToCartButton: {
        color: "secondary",
        css: css`
            white-space: nowrap;
            width: 100%;
            padding: 0 10px;
        `, // keep text from wrapping
    },
    actionHideLink: {
        css: css`
            display: block;
            margin: 10px auto 0 auto;
        `,
    },
    actionDeleteButton: {
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    actionLeaveButton: {
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    sharingStatus: {
        privateTooltip: {
            cssOverrides: {
                tooltipContainer: css`
                    transform: translateX(-33%);
                `,
                tooltipArrow: css`
                    left: 33%;
                `,
            },
        },
    },
    basicIconGridContainer: {
        gap: 10,
        css: css`
            width: 15px;
            flex-shrink: 0;
        `,
        offsetCss: css`
            flex-direction: column;
        `,
    },
    actionFavoriteClickable: {
        css: css`
            width: 15px;
        `,
    },
    starIcon: {
        src: Star,
        size: 15,
    },
    starXIcon: {
        src: StarX,
        size: 15,
    },
    tagTooltip: {
        iconProps: {
            src: Tag,
            size: 15,
            css: css`
                margin: 4px 0;
            `,
        },
    },
};

const WishListCard = ({
    language,
    extendedStyles,
    wishList,
    allowEditingOfWishLists,
    isUpdatingCart,
    addWishListToCart,
    deleteWishList,
    leaveWishList,
    toggleFavorite,
    toggleHide,
    // XNG-Change
    isMiniView,
    wishListURL,
    setListLoadingAction,
    SetInsufficientModalIOpen,
}: Props) => {
    // XNG-Change
    const history = useHistory();
    const toasterContext = useContext(ToasterContext);
    const isLoadingAnyList = useAppSelector(state => Object.keys(state.data.wishLists.isLoading).length > 0);

    const clickAddToCartHandler = (e: any) => {
        e.preventDefault();
        // XNG-Changes
        setListLoadingAction({ isLoading: true, wishList } as any);
        addWishListToCart?.({
            apiParameter: { wishListId: wishList.id },
            onSuccess: (result: CartLineCollectionModel) => {
                onAddAllToCartSuccess(result);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.(resultProps.apiResult);
                } else if (resultProps.errorMessage) {
                    toasterContext.addToast({
                        body: resultProps.errorMessage,
                        messageType: "danger",
                    });
                }
                // <XNG-Changes>
                setListLoadingAction({ isLoading: false } as any);
                // </XNG-Changes>
            },
        });
    };

    const onAddAllToCartSuccess = (result: CartLineCollectionModel) => {
        // <XNG-Changes>
        setListLoadingAction({ isLoading: false } as any);
        if ((result as any)?.availabilities?.length > 0) {
            SetInsufficientModalIOpen({ isOpen: true, modalType: "wishList" });
            return;
        }
        // </XNG-Changes>
        toasterContext.addToast({
            body: result.notAllAddedToCart
                ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                : translate("List Added to Cart"),
            messageType: "success",
        });
    };

    const updatedOnDisplay = getLocalizedDateTime({
        dateTime: new Date(wishList.updatedOn),
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    let lastUpdatedDisplay = `${translate("Updated")} ${updatedOnDisplay}`;
    if (wishList.updatedByDisplayName && !wishList.isAutogenerated) {
        lastUpdatedDisplay += ` ${translate("by")} ${wishList.updatedByDisplayName}`;
    }

    const canAddToCart = wishList.canAddToCart && !!wishList.wishListLinesCount && wishList.wishListLinesCount > 0;
    const addToCartLabel = translate("Add List to Cart");
    const toggleHideButtonText = wishList.isHidden ? translate("Unhide List") : translate("Hide List");
    // XNG-change: making the label more concise
    const toggleFavoriteLabel = wishList.isFavorite ? translate("Unfavorite") : translate("Add to favorites");
    const deleteLabel = translate("Delete");
    const leaveLabel = translate("Leave List");
    const [styles] = React.useState(() => mergeToNew(wishListCardStyles, extendedStyles));

    // XNG-Change
    const openWishlist = () => {
        history.push(`${wishListURL}?id=${wishList.id}`);
    };

    // XNG-Changes: Changed structure
    return isMiniView ? (
        <StyledWrapper data-test-selector="wishListCard" {...styles.cardContainer} onClick={openWishlist}>
            <StyledWrapper data-test-selector="productImagesGridItem" {...styles.miniProductImage}>
                <LazyImage
                    {...styles.miniProductImage}
                    src={wishList.wishListLineCollection?.[0]?.smallImagePath}
                    altText={wishList.wishListLineCollection?.[0]?.altText || wishList.description}
                    title={wishList.wishListLineCollection?.[0]?.altText}
                />
            </StyledWrapper>
            <Typography {...styles.title}>{wishList.name}</Typography>
            <Typography {...styles.miniLastUpdatedText} data-test-selector="wishListCardLastUpdated">
                {lastUpdatedDisplay}
            </Typography>
        </StyledWrapper>
    ) : (
        <StyledWrapper {...styles.container} data-test-selector="wishListCard">
            <StyledWrapper {...styles.descriptionWrapper} data-test-selector={`${wishList.name}`}>
                <StyledWrapper {...styles.nameWrapper}>
                    <MyListsDetailsPageTypeLink
                        title={wishList.name}
                        wishListId={wishList.id}
                        testSelector="wishListCardName"
                    />
                </StyledWrapper>

                <Typography {...styles.lastUpdatedText} data-test-selector="wishListCardLastUpdated">
                    {lastUpdatedDisplay}
                </Typography>
                {wishList.description && (
                    <Typography {...styles.descriptionText} data-test-selector="wishListCardDescription">
                        {wishList.description}
                    </Typography>
                )}
                <StyledWrapper {...styles.favTagsWrapper}>
                    {!wishList.isAutogenerated && (
                        <Clickable
                            {...styles.actionFavoriteClickable}
                            onClick={toggleFavorite}
                            data-test-selector={`myListsFavorite_${wishList.id}`}
                            disabled={isLoadingAnyList}
                            {...{ isFavorite: wishList.isFavorite }}
                        >
                            {wishList.isFavorite && (
                                <>
                                    <Icon
                                        {...styles.starXIcon}
                                        data-test-selector={`myListsFavoriteStarXIcon_${wishList.id}`}
                                    />
                                    {toggleFavoriteLabel}
                                </>
                            )}
                            {!wishList.isFavorite && (
                                <>
                                    <Icon
                                        {...styles.starIcon}
                                        data-test-selector={`myListsFavoriteStarIcon_${wishList.id}`}
                                    />
                                    {toggleFavoriteLabel}
                                </>
                            )}
                        </Clickable>
                    )}
                    {wishList.wishListTags && wishList.wishListTags.length > 0 && (
                        <Tooltip
                            {...styles.tagTooltip}
                            text={`${wishList.wishListTags.map(o => o.tag).join(", ")}`}
                            // XNG-Changes
                            triggerComponent={
                                <Clickable
                                    {...styles.actionFavoriteClickable}
                                    data-test-selector={`myListsTags_${wishList.id}`}
                                    aria-label={translate("Tags")}
                                >
                                    <Icon
                                        {...styles.tagIcon}
                                        data-test-selector={`myListsFavoriteStarIcon_${wishList.id}`}
                                    />
                                    <Typography>{translate("Tags")}</Typography>
                                </Clickable>
                            }
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.imagesWrapper} data-test-selector="productImagesGridItem">
                {wishList.wishListLineCollection &&
                    wishList.wishListLineCollection.length > 0 &&
                    wishList.wishListLineCollection.slice(0, 3).map(line => (
                        <Link key={line.id.toString()} {...styles.productImageLink} href={line.productUri}>
                            <LazyImage
                                {...styles.productImage}
                                src={line.smallImagePath}
                                altText={line.altText}
                                title={line.altText}
                            />
                        </Link>
                    ))}
            </StyledWrapper>
            <StyledWrapper {...styles.sharingStatusWrapper}>
                <WishListSharingStatus extendedStyles={styles.sharingStatus} wishList={wishList} />
                {!!wishList.wishListLinesCount && wishList.wishListLinesCount > 0 && (
                    <SmallHeadingAndText
                        heading={translate("Items")}
                        text={(wishList as any).wishListLinesCount}
                        extendedStyles={styles.itemsSmallHeading}
                    />
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.actionsWrapper}>
                {/* <Hidden above="sm">
                    <OverflowMenu position="end" {...styles.actionOverflowMenu}>
                        <Clickable
                            {...styles.actionAddToCartClickable}
                            disabled={!canAddToCart || isUpdatingCart}
                            onClick={clickAddToCartHandler}
                        >
                            {addToCartLabel}
                        </Clickable>
                        {!wishList.isAutogenerated && (
                            <Clickable {...styles.actionHideClickable} disabled={isLoadingAnyList} onClick={toggleHide}>
                                {toggleHideButtonText}
                            </Clickable>
                        )}
                        {allowEditingOfWishLists &&
                            !!deleteWishList &&
                            !wishList.isSharedList &&
                            !wishList.isAutogenerated && (
                                <Clickable {...styles.actionDeleteClickable} onClick={deleteWishList}>
                                    {deleteLabel}
                                </Clickable>
                            )}
                        {allowEditingOfWishLists &&
                            !!leaveWishList &&
                            wishList.isSharedList &&
                            wishList.shareOption !== ShareOptions.AllCustomerUsers && (
                                <Clickable {...styles.actionLeaveClickable} onClick={leaveWishList}>
                                    {leaveLabel}
                                </Clickable>
                            )}
                    </OverflowMenu>
                </Hidden> */}
                <Button
                    {...styles.actionAddToCartButton}
                    disabled={!canAddToCart || isUpdatingCart}
                    data-test-selector={`wishListAddToCartButton_${wishList.id}`}
                    onClick={clickAddToCartHandler}
                    aria-label={addToCartLabel}
                >
                    {addToCartLabel}
                </Button>
                <StyledWrapper {...styles.additionalActions}>
                    {!!deleteWishList &&
                        !wishList.isSharedList &&
                        !wishList.isAutogenerated &&
                        allowEditingOfWishLists && (
                            <Link {...styles.actionDeleteLink} onClick={deleteWishList} aria-label={deleteLabel}>
                                {deleteLabel}
                            </Link>
                        )}
                    {!wishList.isAutogenerated && (
                        <Link
                            {...styles.actionHideLink}
                            onClick={toggleHide}
                            data-test-selector={`myListsHide_${wishList.id}`}
                            disabled={isLoadingAnyList}
                            icon={{
                                position: "left",
                                iconProps: {
                                    src: wishList.isHidden ? "EyeOff" : "Eye",
                                },
                            }}
                        >
                            {toggleHideButtonText}
                        </Link>
                    )}
                    {allowEditingOfWishLists &&
                        !!leaveWishList &&
                        wishList.isSharedList &&
                        wishList.shareOption !== ShareOptions.AllCustomerUsers && (
                            <Link
                                {...styles.actionLeaveLink}
                                onClick={leaveWishList}
                                data-test-selector={`wishListCardLeaveButton_${wishList.id}`}
                            >
                                {leaveLabel}
                            </Link>
                        )}
                </StyledWrapper>
                {/*
                    {allowEditingOfWishLists &&
                        !!deleteWishList &&
                        !wishList.isSharedList &&
                        !wishList.isAutogenerated && (
                            <Button
                                {...styles.actionDeleteButton}
                                onClick={deleteWishList}
                                data-test-selector={`wishListCardDeleteButton_${wishList.id}`}
                            >
                                {deleteLabel}
                            </Button>
                        )}
                   */}
            </StyledWrapper>
        </StyledWrapper>
    );
};

// XNG-Changes: add mapDispatchToProps
export default connect(mapStateToProps, mapDispatchToProps)(WishListCard);
